export const productionPlaywrightScript = `
  import { createSession } from "@plutoxyz/automation";
  import { chromium } from "playwright-core";

  const session = await createSession();

  const input = await session.prompt({
      title: "Login to Pseudo Bank",
      description: "Enter your username and password to login to Pseudo Bank",
      prompts: [
          { label: "Username", type: "text", attributes: {} },
          { label: "Password", type: "password", attributes: {} },
      ],
  });

  const browser = await chromium.connectOverCDP(await session.cdp());
  const context = browser.contexts()[0];
  const page = context.pages()[0];

  await page.goto("https://pseudo-bank.pluto.dev");
  await page.getByRole("textbox", { name: "Username" }).fill(input[0]);
  await page.getByRole("textbox", { name: "Password" }).fill(input[1]);
  await page.getByRole("button", { name: "Login" }).click();
  await page.waitForSelector("text=Your Accounts", { timeout: 5000 });

  const balanceLocator = page.locator("#balance-2");
  await balanceLocator.waitFor({ state: "visible", timeout: 5000 });
  const balanceText = (await balanceLocator.textContent()) || "";
  const balance = parseFloat(balanceText.replace(/[$,]/g, ""));

  await session.prove("bank_balance", balance);

  console.log("🎉 Session complete");

  await page.close();
  await browser.close();
  await session.close();
`;

export const developmentPlaywrightScript = `
import { createSession } from "@plutoxyz/automation";
import { chromium } from "playwright-core";

const session = await createSession();

const input = await session.prompt({
    title: "Login to Pseudo Bank",
    description: "Enter your username and password to login to Pseudo Bank",
    prompts: [
        { label: "Username", type: "text", attributes: {} },
        { label: "Password", type: "password", attributes: {} },
    ],
});

const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];

await page.goto("https://pseudo-bank.pluto.dev");
await page.getByRole("textbox", { name: "Username" }).fill(input[0]);
await page.getByRole("textbox", { name: "Password" }).fill(input[1]);
await page.getByRole("button", { name: "Login" }).click();
await page.waitForSelector("text=Your Accounts", { timeout: 5000 });

const balanceLocator = page.locator("#balance-2");
await balanceLocator.waitFor({ state: "visible", timeout: 5000 });
const balanceText = (await balanceLocator.textContent()) || "";
const balance = parseFloat(balanceText.replace(/[$,]/g, ""));

await session.prove("bank_balance", balance);

console.log("🎉 Session complete");

await page.close();
await browser.close();
await session.close();
`;

// TODO(matt) remove this from here?
export const venmoTransactionsProtoPlaywrightScript = `
import { createSession } from "@plutoxyz/automation";
import { chromium } from 'playwright-core'

const session = await createSession()

const input = await session.prompt({
  title: 'Login to Venmo',
  description: 'Enter your Venmo credentials to prove your transactions',
  prompts: [
    {
      label: 'Email',
      type: 'text',
      attributes: {
        min_length: 3,
        max_length: 3,
        placeholder: 'Email or Phone Number'
      }
    },
    {
      label: 'Password',
      type: 'password',
      attributes: {
        placeholder: 'Password'
      }
    }
  ]
})

const browser = await chromium.connectOverCDP(await session.cdp());
const context = browser.contexts()[0];
const page = context.pages()[0];

await page.goto('https://id.venmo.com/signin')

await page.getByRole('textbox', { name: 'email' }).fill(input[0])
await page.getByRole('button', { name: 'Next' }).click()

await page.getByRole('textbox', { name: 'Password', timeout: 10000 }).fill(input[1])
await page.getByRole('button', { name: 'Log in' }).click()

await page.waitForSelector('h1', { timeout: 10000 })
const security = await page
  .getByRole('heading', { name: 'Confirm your identity' })
  .isVisible()
  .catch(() => false)

if (security) {
  await page.waitForTimeout(2000)
  await page.getByRole('button', { name: 'Send code' }).click()

  const otp = await session.prompt({
    title: 'Enter one time code',
    description: 'Enter the one time code sent to your phone',
    prompts: [
      {
        label: 'Code',
        type: 'number_code',
        attributes: {
          length: 6
        }
      }
    ]
  })

  await page.fill('#confirm-input', otp[0])
  await page.getByRole('button', { name: 'Confirm it' }).click()
}

// Wait for the feed page to load
await page.waitForSelector('#feed-tab-0, #feed-tab-1', { timeout: 10000 })

// Click the "mine" tab to show personal transactions
await page.click('#feed-tab-1')

// Wait for the personal transactions to load
await page.waitForSelector('[data-testid="me-feed-container"]', { timeout: 10000 })

// Let the page fully load
await page.waitForTimeout(5000)

// Scrape the transactions
const transactions = await page.evaluate(() => {
  const transactionData = []

  // Get all the article elements in the feed
  const articles = document.querySelectorAll('[data-testid="me-feed-container"] article')
  // Process each article
  articles.forEach((article) => {
    // Extract the headline using the id pattern which is more stable
    const headlineElement = article.querySelector('[id^="storyHeadlineId-"]')

    // Get transaction content using attribute selectors and structural position
    // Content is typically in a div that's a sibling to the div containing the timestamp
    let contentElement = null
    const timeElement = article.querySelector('div[aria-label*="ago"]')
    if (timeElement && timeElement.parentElement) {
      // Look for content in siblings of the timestamp's parent
      const parentContainer = timeElement.parentElement.parentElement
      if (parentContainer) {
        // Content is typically the next sibling after the div containing the time
        contentElement = parentContainer.nextElementSibling?.querySelector('div')
      }
    }

    // Get amount using structural position - it's the last direct child div of the article
    // This is the most stable approach as it doesn't rely on class names
    const allTopLevelDivs = article.querySelectorAll(':scope > div')
    const amountElement = allTopLevelDivs[allTopLevelDivs.length - 1]

    if (!headlineElement || !amountElement) return // Skip if missing crucial data

    // Get the text content
    const headline = headlineElement.textContent?.trim() || ''
    const content = contentElement?.textContent?.trim() || ''
    const amount = amountElement.textContent?.trim() || ''

    // Extract the timestamp from the time element
    const timestamp = timeElement ? timeElement.getAttribute('aria-label') || timeElement.textContent?.trim() : ''

    // Determine if this is a sent, received, or transfer transaction
    let type = 'unknown'
    if (amount.startsWith('+')) {
      type = 'received'
    } else if (amount.startsWith('-')) {
      type = 'sent'
    } else if (headline.includes('Transfer')) {
      type = 'transfer'
    }

    // Format the transaction description nicely
    let description = headline
    if (headline.includes('You paid')) {
      description = headline.replace('You paid', 'You sent money to')
    } else if (headline.includes('paid you')) {
      description = headline.replace('paid you', 'sent money to you')
    }

    // Remove positive/negative signs from amount
    const cleanAmount = amount.replace(/^[+-]\s*/, '')

    // Create a nice formatted summary with amount
    const fullSummary = headline + ' - ' + cleanAmount + (content ? ' (' + content + ')' : '')
    const summary = headline + ' - ' + cleanAmount
    transactionData.push({
      summary,
      type,
      amount,
      headline,
      content,
      timestamp
    })
  })

  return transactionData
})

// Get only recent transactions (up to 5)
const recentTransactions = transactions.slice(0, 5)

if (recentTransactions.length === 0) {
  await session.prove('venmo_transactions', {
    transactions: [],
    multiple_transactions: []
  })
} else {
  // Format transactions for display
  const formattedTransactions = recentTransactions.map((tx) => tx.summary)

  // Ask user to select transactions
  const selectResult = await session.prompt({
    title: 'Verify Venmo Transactions',
    description: 'Select transaction(s) you want to verify',
    prompts: [
      {
        label: 'Select Transactions',
        type: 'checkbox',
        required: false,
        attributes: {
          options: formattedTransactions,
          multiple: false, // Multi-transaction selection
          min: 1,
          max: 5
        }
      }
    ]
  })

  // Parse the JSON strings back to arrays
  const multipleSelection = selectResult[0] ? JSON.parse(selectResult[0]) : []
  const selectedMultipleTransactions = recentTransactions.filter((tx) => multipleSelection.includes(tx.summary))

  // Prove the selections
  await session.prove('venmo_transactions', selectedMultipleTransactions)
}

console.log('🎉 Session complete')

await page.close()
await browser.close()
await session.close()
`;
